import { Service, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, defer, map } from 'rxjs';
import { environment } from '../../environment/environment';
import { extraireReponse } from './api';
import { ReponseApi } from './api.types';
import { ErreurApi } from './api-error';
import { Authentification } from './authentification';
import { CategorieIntervention, TYPES_PHOTOS_INTERVENTION } from './intervention-edition';
import { PhotoIntervention, estPhotoIntervention } from './intervention-api';

/** Référence suffisante pour lire les métadonnées, sans imposer le contrat complet d’un bon. */
export type ReferencePhotosIntervention = { id: number; categorie: CategorieIntervention };

/**
 * Lit les métadonnées puis les fichiers via HttpClient, sans cache applicatif.
 * L’intercepteur commun fournit l’authentification habituelle : Authorization et X-JWT-Authorization.
 */
@Service()
export class InterventionPhotos {
  private readonly http = inject(HttpClient);
  readonly sessionChanges = inject(Authentification).sessionChanges;

  /** Lit le tableau photos de l’intervention et vérifie sa structure avant tout téléchargement. */
  lister({ id, categorie }: ReferencePhotosIntervention): Observable<PhotoIntervention[]> {
    return defer(() => {
      if (!Number.isSafeInteger(id) || id <= 0 || !['curative', 'preventive'].includes(categorie)) {
        throw new ErreurApi('Intervention non identifiée.', 'reponse_invalide');
      }
      return this.http.get<ReponseApi<{ photos: unknown }>>(
        `${environment.apiUrl}/intervention/${categorie}/${id}`,
      );
    }).pipe(
      map((reponse) => {
        const photos = extraireReponse(reponse)?.photos;
        if (!Array.isArray(photos) || !photos.every(estPhotoIntervention)) {
          throw new ErreurApi('La liste des photos reçue est invalide.', 'reponse_invalide');
        }
        return photos;
      }),
    );
  }

  /** Télécharge le binaire authentifié et refuse les réponses vides ou d’un type inattendu. */
  charger(photo: PhotoIntervention): Observable<Blob> {
    return defer(() =>
      this.http.get(this.urlPhoto(photo.url), {
        headers: new HttpHeaders({ Accept: TYPES_PHOTOS_INTERVENTION.join(', ') }),
        responseType: 'blob',
      }),
    ).pipe(
      map((blob) => {
        if (!blob.size || !TYPES_PHOTOS_INTERVENTION.includes(blob.type)) {
          throw new ErreurApi(
            'Le fichier reçu n’est pas une image JPEG, PNG ou WebP valide.',
            'reponse_invalide',
          );
        }
        return blob;
      }),
    );
  }

  /** Résout les URL relatives à l’API et n’autorise que ses routes photo, dont l’ancienne route bon. */
  private urlPhoto(chemin: string): string {
    const base = new URL(`${environment.apiUrl.replace(/\/$/, '')}/`);
    const url = new URL(chemin.startsWith('/intervention/') ? chemin.slice(1) : chemin, base);
    // Le JWT ne doit jamais être transmis à un domaine indiqué arbitrairement dans la réponse.
    const route = url.pathname.startsWith(base.pathname)
      ? url.pathname.slice(base.pathname.length)
      : '';
    if (
      url.origin !== base.origin ||
      url.username ||
      url.password ||
      url.hash ||
      !/^intervention\/(curative|preventive|bon)\/[1-9]\d*\/photo\/[1-9]\d*$/.test(route)
    ) {
      throw new ErreurApi('L’adresse de cette photo est invalide.', 'reponse_invalide');
    }
    return url.href;
  }
}
